import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  CreateExpertWorkspaceRequest,
  ExpertWorkspaceRecord,
  ExpertWorkspaceService,
  PurchaseExpertWorkspaceSubscriptionPlanRequest,
  SwitchExpertWorkspaceSubscriptionPlanRequest,
  UpdateExpertWorkspaceCodeRequest,
  UpdateExpertWorkspaceExpertBusinessHoursRequest,
  UpdateExpertWorkspaceExpertExcludedNotificationsRequest,
  UpdateExpertWorkspaceExpertScheduleReservationRequest,
  UpdateExpertWorkspaceImageRequest,
  UpdateExpertWorkspaceNameRequest,
  UpdateExpertWorkspaceNextSubscriptionPlanRequest,
} from '@libs/shared/api';
import { BasicSnackbarService } from '@libs/shared/ui/basic-snackbar/basic-snackbar.service';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { extractErrorMessage } from '@libs/shared/utils/functions/error-message.util';
import { pipe, Subject, switchMap } from 'rxjs';

interface ExpertWorkspaceCoreState {
  content: ExpertWorkspaceRecord;
  isLoading: boolean;
  isLoaded: boolean;
  errorMessage: string;
}

const initialState: ExpertWorkspaceCoreState = {
  content: undefined,
  isLoading: false,
  isLoaded: false,
  errorMessage: '',
};

@Injectable({
  providedIn: 'root',
})
export class CurrentExpertWorkspaceCoreStore {
  private readonly expertWorkspaceService = inject(ExpertWorkspaceService);
  private readonly snackBar = inject(BasicSnackbarService);
  private readonly router = inject(Router);

  readonly state = signalState(initialState);
  readonly content$$ = this.state.content;
  readonly isLoading$$ = this.state.isLoading;
  readonly isLoaded$$ = this.state.isLoaded;
  readonly errorMessage$$ = this.state.errorMessage;

  constructor() {}

  readonly created$ = new Subject<ExpertWorkspaceRecord>();
  readonly updated$ = new Subject<ExpertWorkspaceRecord>();
  readonly activated$ = new Subject<ExpertWorkspaceRecord>();
  readonly inactivated$ = new Subject<ExpertWorkspaceRecord>();

  setExpertWorkspace(record: ExpertWorkspaceRecord) {
    patchState(this.state, {
      content: record,
      isLoading: false,
      isLoaded: true,
      errorMessage: '',
    });
  }

  fetchExpertWorkspaceById = rxMethod<{
    id: string;
    eventType?: 'created' | 'updated' | 'activated' | 'inactivated';
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService.fetchExpertWorkspaceById(payload.id).pipe(
          tapResponse(
            (ExpertWorkspace: ExpertWorkspaceRecord) => {
              patchState(this.state, {
                content: ExpertWorkspace,
                isLoading: false,
                isLoaded: true,
                errorMessage: '',
              });

              switch (payload.eventType) {
                case 'created':
                  this.created$.next(ExpertWorkspace);
                  break;
                case 'updated':
                  this.updated$.next(ExpertWorkspace);
                  break;
                case 'activated':
                  this.activated$.next(ExpertWorkspace);
                  break;
                case 'inactivated':
                  this.inactivated$.next(ExpertWorkspace);
                  break;
              }
            },
            (e: HttpErrorResponse) => {
              patchState(this.state, {
                content: undefined,
                isLoading: false,
                isLoaded: false,
                errorMessage: extractErrorMessage(e),
              });
            }
          )
        )
      )
    )
  );

  create = rxMethod<{
    request: CreateExpertWorkspaceRequest;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService.createExpertWorkspace(payload.request).pipe(
          tapResponse(
            (ExpertWorkspaceId: string) => {
              if (payload.showSuccessSnackbar) {
                this.snackBar.open('센터가 생성되었습니다.');
              }
              this.fetchExpertWorkspaceById({
                id: ExpertWorkspaceId,
                eventType: 'created',
              });
            },
            (e: HttpErrorResponse) => {
              if (payload.showFailureSnackbar) {
                this.snackBar.open('센터 생성에 실패했습니다.');
              }
              patchState(this.state, {
                errorMessage: extractErrorMessage(e),
              });
            }
          )
        )
      )
    )
  );

  activate = rxMethod<{
    id: string;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService.activateExpertWorkspace(payload.id).pipe(
          tapResponse(
            (expertWorkspaceId: string) => {
              if (payload.showSuccessSnackbar) {
                this.snackBar.open('센터가 활성화되었습니다.');
              }
              this.fetchExpertWorkspaceById({
                id: expertWorkspaceId,
                eventType: 'activated',
              });
            },
            (e: HttpErrorResponse) => {
              if (payload.showFailureSnackbar) {
                this.snackBar.open('센터 활성화에 실패했습니다.');
              }
              patchState(this.state, {
                errorMessage: extractErrorMessage(e),
              });
            }
          )
        )
      )
    )
  );

  inactivate = rxMethod<{
    id: string;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService.inactivateExpertWorkspace(payload.id).pipe(
          tapResponse(
            (expertWorkspaceId: string) => {
              if (payload.showSuccessSnackbar) {
                this.snackBar.open('센터가 비활성화되었습니다.');
              }
              this.fetchExpertWorkspaceById({
                id: expertWorkspaceId,
                eventType: 'inactivated',
              });
            },
            (e: HttpErrorResponse) => {
              if (payload.showFailureSnackbar) {
                this.snackBar.open('센터 비활성화에 실패했습니다.');
              }
              patchState(this.state, {
                errorMessage: extractErrorMessage(e),
              });
            }
          )
        )
      )
    )
  );

  updateCode = rxMethod<{
    id: string;
    request: UpdateExpertWorkspaceCodeRequest;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService
          .updateExpertWorkspaceCode(payload.id, payload.request)
          .pipe(
            tapResponse(
              (expertWorkspaceId: string) => {
                if (payload.showSuccessSnackbar) {
                  this.snackBar.open('센터 아이디가 수정되었습니다.');
                }
                this.fetchExpertWorkspaceById({
                  id: expertWorkspaceId,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.snackBar.open('센터 아이디 수정에 실패했습니다.');
                }
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  updateName = rxMethod<{
    id: string;
    request: UpdateExpertWorkspaceNameRequest;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService
          .updateExpertWorkspaceName(payload.id, payload.request)
          .pipe(
            tapResponse(
              (expertWorkspaceId: string) => {
                if (payload.showSuccessSnackbar) {
                  this.snackBar.open('센터 이름이 수정되었습니다.');
                }
                this.fetchExpertWorkspaceById({
                  id: expertWorkspaceId,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.snackBar.open('센터 아이디 수정에 실패했습니다.');
                }
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  updateImage = rxMethod<{
    id: string;
    request: UpdateExpertWorkspaceImageRequest;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService
          .updateExpertWorkspaceImage(payload.id, payload.request)
          .pipe(
            tapResponse(
              (expertWorkspaceId: string) => {
                if (payload.showSuccessSnackbar) {
                  this.snackBar.open('센터 이미지가 수정되었습니다.');
                }
                this.fetchExpertWorkspaceById({
                  id: expertWorkspaceId,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.snackBar.open('센터 이미지 수정에 실패했습니다.');
                }
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  openExpertWorkspaceExpertJoinInquiry = rxMethod<{
    id: string;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService
          .openExpertWorkspaceExpertJoinInquiry(payload.id)
          .pipe(
            tapResponse(
              (expertWorkspaceId: string) => {
                if (payload.showSuccessSnackbar) {
                  this.snackBar.open('가입 문의가 열렸습니다.');
                }
                this.fetchExpertWorkspaceById({
                  id: expertWorkspaceId,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.snackBar.open('가입 문의 열기에 실패했습니다.');
                }
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  acceptExpertWorkspaceExpertJoinInquiry = rxMethod<{
    id: string;
    expertWorkspaceExpertId: string;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService
          .acceptExpertWorkspaceExpertJoinInquiry(
            payload.id,
            payload.expertWorkspaceExpertId
          )
          .pipe(
            tapResponse(
              (expertWorkspaceId: string) => {
                if (payload.showSuccessSnackbar) {
                  this.snackBar.open('가입 문의가 수락되었습니다.');
                }
                this.fetchExpertWorkspaceById({
                  id: expertWorkspaceId,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.snackBar.open('가입 문의 수락에 실패했습니다.');
                }
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  declineExpertWorkspaceExpertJoinInquiry = rxMethod<{
    id: string;
    expertWorkspaceExpertId: string;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService
          .declineExpertWorkspaceExpertJoinInquiry(
            payload.id,
            payload.expertWorkspaceExpertId
          )
          .pipe(
            tapResponse(
              (expertWorkspaceId: string) => {
                if (payload.showSuccessSnackbar) {
                  this.snackBar.open('가입 문의가 거절되었습니다.');
                }
                this.fetchExpertWorkspaceById({
                  id: expertWorkspaceId,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.snackBar.open('가입 문의 거절에 실패했습니다.');
                }
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  removeExpertWorkspaceExpert = rxMethod<{
    id: string;
    expertWorkspaceExpertId: string;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService
          .removeExpertWorkspaceExpert(
            payload.id,
            payload.expertWorkspaceExpertId
          )
          .pipe(
            tapResponse(
              (expertWorkspaceId: string) => {
                if (payload.showSuccessSnackbar) {
                  this.snackBar.open('참가자가 삭제되었습니다.');
                }
                this.fetchExpertWorkspaceById({
                  id: expertWorkspaceId,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.snackBar.open('참가자 삭제에 실패했습니다.');
                }
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  updateExpertWorkspaceExpertBusinessHours = rxMethod<{
    id: string;
    expertWorkspaceExpertId: string;
    request: UpdateExpertWorkspaceExpertBusinessHoursRequest;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService
          .updateExpertWorkspaceExpertBusinessHours(
            payload.id,
            payload.expertWorkspaceExpertId,
            payload.request
          )
          .pipe(
            tapResponse(
              (expertWorkspaceId: string) => {
                if (payload.showSuccessSnackbar) {
                  this.snackBar.open('근무 시간이 성공적으로 변경되었습니다.');
                }
                this.fetchExpertWorkspaceById({
                  id: expertWorkspaceId,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.snackBar.open('근무 시간 변경에 실패했습니다.');
                }
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  enableExpertWorkspaceExpertSessionSignature = rxMethod<{
    id: string;
    expertWorkspaceExpertId: string;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService
          .enableExpertWorkspaceExpertSessionSignature(
            payload.id,
            payload.expertWorkspaceExpertId
          )
          .pipe(
            tapResponse(
              (expertWorkspaceId: string) => {
                if (payload.showSuccessSnackbar) {
                  this.snackBar.open('운동 서명이 활성화되었습니다.');
                }
                this.fetchExpertWorkspaceById({
                  id: expertWorkspaceId,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.snackBar.open('운동 서명 활성화에 실패했습니다.');
                }
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  disableExpertWorkspaceExpertSessionSignature = rxMethod<{
    id: string;
    expertWorkspaceExpertId: string;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService
          .disableExpertWorkspaceExpertSessionSignature(
            payload.id,
            payload.expertWorkspaceExpertId
          )
          .pipe(
            tapResponse(
              (expertWorkspaceId: string) => {
                if (payload.showSuccessSnackbar) {
                  this.snackBar.open('운동 서명이 비활성화되었습니다.');
                }
                this.fetchExpertWorkspaceById({
                  id: expertWorkspaceId,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.snackBar.open('운동 서명 비활성화에 실패했습니다.');
                }
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  enableExpertWorkspaceAttendanceKiosk = rxMethod<{
    id: string;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService
          .enableExpertWorkspaceAttendanceKiosk(payload.id)
          .pipe(
            tapResponse(
              (expertWorkspaceId: string) => {
                if (payload.showSuccessSnackbar) {
                  this.snackBar.open('출입 키오스크가 활성화되었습니다.');
                }
                this.fetchExpertWorkspaceById({
                  id: expertWorkspaceId,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.snackBar.open('출입 키오스크 활성화에 실패했습니다.');
                }
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  disableExpertWorkspaceAttendanceKiosk = rxMethod<{
    id: string;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService
          .disableExpertWorkspaceAttendanceKiosk(payload.id)
          .pipe(
            tapResponse(
              (expertWorkspaceId: string) => {
                if (payload.showSuccessSnackbar) {
                  this.snackBar.open('출입 키오스크가 비활성화되었습니다.');
                }
                this.fetchExpertWorkspaceById({
                  id: expertWorkspaceId,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.snackBar.open('출입 키오스크 비활성화에 실패했습니다.');
                }
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  updateExpertWorkspaceExpertExcludedNotifications = rxMethod<{
    id: string;
    expertWorkspaceExpertId: string;
    request: UpdateExpertWorkspaceExpertExcludedNotificationsRequest;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService
          .updateExpertWorkspaceExpertExcludedNotifications(
            payload.id,
            payload.expertWorkspaceExpertId,
            payload.request
          )
          .pipe(
            tapResponse(
              (expertWorkspaceId: string) => {
                if (payload.showSuccessSnackbar) {
                  this.snackBar.open(
                    '알림 수신 여부가 성공적으로 변경되었습니다.'
                  );
                }
                this.fetchExpertWorkspaceById({
                  id: expertWorkspaceId,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.snackBar.open('알림 수신 여부 변경에 실패했습니다.');
                }
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  updateExpertWorkspaceExpertScheduleReservation = rxMethod<{
    id: string;
    expertWorkspaceExpertId: string;
    request: UpdateExpertWorkspaceExpertScheduleReservationRequest;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService
          .updateExpertWorkspaceExpertScheduleReservation(
            payload.id,
            payload.expertWorkspaceExpertId,
            payload.request
          )
          .pipe(
            tapResponse(
              (expertWorkspaceId: string) => {
                if (payload.showSuccessSnackbar) {
                  this.snackBar.open('일정 예약이 성공적으로 변경되었습니다.');
                }
                this.fetchExpertWorkspaceById({
                  id: expertWorkspaceId,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.snackBar.open('일정 예약 변경에 실패했습니다.');
                }
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  purchaseExpertWorkspaceSubscriptionPlan = rxMethod<{
    id: string;
    request: PurchaseExpertWorkspaceSubscriptionPlanRequest;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService
          .purchaseExpertWorkspaceSubscriptionPlan(payload.id, payload.request)
          .pipe(
            tapResponse(
              (expertWorkspaceId: string) => {
                if (payload.showSuccessSnackbar) {
                  this.snackBar.open('구독권이 성공적으로 구매되었습니다.');
                }
                this.fetchExpertWorkspaceById({
                  id: expertWorkspaceId,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.snackBar.open('구독권 구매에 실패했습니다.');
                }
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  switchExpertWorkspaceSubscriptionPlan = rxMethod<{
    id: string;
    request: SwitchExpertWorkspaceSubscriptionPlanRequest;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService
          .switchExpertWorkspaceSubscriptionPlan(payload.id, payload.request)
          .pipe(
            tapResponse(
              (expertWorkspaceId: string) => {
                if (payload.showSuccessSnackbar) {
                  this.snackBar.open('구독권이 성공적으로 변경되었습니다.');
                }
                this.fetchExpertWorkspaceById({
                  id: expertWorkspaceId,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.snackBar.open('구독권 변경에 실패했습니다.');
                }
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  updateExpertWorkspaceNextSubscriptionPlan = rxMethod<{
    id: string;
    request: UpdateExpertWorkspaceNextSubscriptionPlanRequest;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService
          .updateExpertWorkspaceNextSubscriptionPlan(
            payload.id,
            payload.request
          )
          .pipe(
            tapResponse(
              (expertWorkspaceId: string) => {
                if (payload.showSuccessSnackbar) {
                  this.snackBar.open(
                    '다음 구독권이 성공적으로 변경되었습니다.'
                  );
                }
                this.fetchExpertWorkspaceById({
                  id: expertWorkspaceId,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.snackBar.open('다음 구독권 변경에 실패했습니다.');
                }
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  cancelExpertWorkspaceNextSubscriptionPlan = rxMethod<{
    id: string;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService
          .cancelExpertWorkspaceNextSubscriptionPlan(payload.id)
          .pipe(
            tapResponse(
              (expertWorkspaceId: string) => {
                if (payload.showSuccessSnackbar) {
                  this.snackBar.open(
                    '다음 구독권이 성공적으로 취소되었습니다.'
                  );
                }
                this.fetchExpertWorkspaceById({
                  id: expertWorkspaceId,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.snackBar.open('다음 구독권 취소에 실패했습니다.');
                }
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );
  terminateExpertWorkspace = rxMethod<{
    id: string;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.expertWorkspaceService.terminateExpertWorkspace(payload.id).pipe(
          tapResponse(
            (expertWorkspaceId: string) => {
              if (payload.showSuccessSnackbar) {
                this.snackBar.open('센터가 성공적으로 삭제되었습니다.');
              }
              this.fetchExpertWorkspaceById({
                id: expertWorkspaceId,
                eventType: 'updated',
              });
              this.router.navigateByUrl('/expert-workspace-selection').then();
            },
            (e: HttpErrorResponse) => {
              if (payload.showFailureSnackbar) {
                this.snackBar.open('센터 삭제에 실패했습니다.');
              }
              patchState(this.state, {
                errorMessage: extractErrorMessage(e),
              });
            }
          )
        )
      )
    )
  );
}
